export interface ApiResponse {
  statusCode: number;
  headers: Record<string, string>;
  body: string;
}

export interface AppResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  timestamp: string;
}

export type PlanType = 'free_trial' | 'pro_trial' | 'pro' | 'institution';
export type SubscriptionStatus = 'active' | 'expired' | 'cancelled';

export interface TierLimits {
  pdfsPerMonth: number;
  maxWritten: number;
  maxMCQ: number;
}

export interface UserSubscription {
  plan: PlanType;
  status: SubscriptionStatus;
  periodStart: string;
  periodEnd: string;
  pdfsGenerated: number;
  pdfsRemaining: number;
  limits: TierLimits;
  hasUsedFreeTrial: boolean;
}

export type PaymentStatus = 'pending' | 'completed' | 'failed' | 'cancelled';

export interface PaymentRecord {
  id: string;
  tranId: string;
  plan: PlanType;
  amount: number;
  status: PaymentStatus;
  createdAt: string;
}

export interface InitiatePaymentResponse {
  tranId: string;
  activated: boolean;
  gatewayUrl?: string;
}

export interface InitiatePaymentResult {
  tranId: string;
  activated: boolean;
  gatewayUrl?: string;
}

// ─── Roles ────────────────────────────────────────────────────────────────────

/**
 * Held in the Supabase user's `app_metadata`, which only the service-role key and
 * the dashboard can write — a user cannot promote themselves. Never read this
 * from `user_metadata`, which is self-writable and therefore worthless for
 * authorization. A missing role means `'user'`.
 */
export type UserRole = 'user' | 'superadmin';

export const SUPERADMIN_ROLE: UserRole = 'superadmin';

// ─── Audit log ────────────────────────────────────────────────────────────────

/** Mirrors the `activity_type` enum in supabase-schema.sql. Change both together. */
export const ACTIVITY_TYPES = [
  'user_signed_up',
  'user_signed_in',
  'question_generated',
  'generation_blocked',
  'generation_failed',
  'payment_initiated',
  'payment_completed',
  'payment_failed',
  'subscription_activated',
  'subscription_cancelled',
  'user_banned',
  'user_unbanned',
] as const;

export type ActivityType = (typeof ACTIVITY_TYPES)[number];

/**
 * Free-form per-event detail. Deliberately metadata only — no question or answer
 * text is ever logged, and `generation_failed` carries an upstream status code
 * without the upstream message, which can contain fragments of our API key.
 */
export type ActivityMetadata = Record<string, string | number | boolean | string[] | null>;

export interface ActivityLogEntry {
  id: string;
  userId: string | null;
  userEmail: string | null;
  type: ActivityType;
  metadata: ActivityMetadata;
  createdAt: string;
}

// ─── Admin ────────────────────────────────────────────────────────────────────

export interface AdminUserSummary {
  id: string;
  email: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  role: UserRole;
  plan: PlanType | null;
  status: SubscriptionStatus | null;
  periodEnd: string | null;
  createdAt: string;
  lastSignInAt: string | null;
  totalGenerations: number;
  /**
   * `auth.users.banned_until`, the authoritative ban flag. Null when never
   * banned; a past timestamp when a ban has since expired. Distinct from
   * `status` above, which is the *subscription* lifecycle.
   */
  bannedUntil: string | null;
}

// ─── Bans ─────────────────────────────────────────────────────────────────────

/**
 * Ban durations are stored in GoTrue as a Go duration string. An indefinite ban
 * is expressed as ~100 years rather than a null, because `banned_until` is the
 * only field the auth layer checks — there is no separate "forever" flag.
 */
export const INDEFINITE_BAN_HOURS = 876_000;

/** Anything expiring beyond this is a `INDEFINITE_BAN_HOURS` ban, not a real date. */
export const INDEFINITE_BAN_THRESHOLD_MS = 50 * 365 * 24 * 60 * 60 * 1000;

export function isIndefiniteBan(bannedUntil: string): boolean {
  return new Date(bannedUntil).getTime() - Date.now() > INDEFINITE_BAN_THRESHOLD_MS;
}

/** True only for a ban that is still in force — an expired `banned_until` is not a ban. */
export function isBanActive(bannedUntil: string | null | undefined): boolean {
  return Boolean(bannedUntil && new Date(bannedUntil).getTime() > Date.now());
}

/**
 * Upper bound on a fixed-length ban. Anything longer is an indefinite ban, which
 * is expressed by omitting `days` rather than by asking for 10,000 of them.
 */
export const BAN_MAX_DAYS = 365;
export const BAN_REASON_MAX_LENGTH = 500;

// ─── Renewal reminders ────────────────────────────────────────────────────────

/**
 * How long an expired subscription stays quiet after a reminder before prompting
 * again. There is no cap on the number of reminders: an account that stays
 * expired keeps being asked, because the prompt stops the moment it works.
 */
export const RENEWAL_REMINDER_INTERVAL_DAYS = 15;

export const RENEWAL_REMINDER_INTERVAL_MS = RENEWAL_REMINDER_INTERVAL_DAYS * 24 * 60 * 60 * 1000;

/**
 * Whether an expired subscription is due to prompt the user to subscribe again.
 *
 * Deliberately measured from the last reminder rather than from `period_end`, so a
 * dismissal always buys a full quiet interval. Anchoring to expiry instead would
 * let someone who dismissed on day 14 be prompted again on day 15.
 *
 * Lives in `common/` because it is the whole rule: whatever delivers the reminder
 * — today the dashboard banner, later a scheduled sweep — must agree on when one
 * is owed, and a second copy of this arithmetic is how the two would drift apart.
 */
export function isRenewalReminderDue(
  status: SubscriptionStatus,
  lastRemindedAt: string | null | undefined,
  now: number = Date.now()
): boolean {
  if (status !== 'expired') return false;
  if (!lastRemindedAt) return true;

  const remindedAt = new Date(lastRemindedAt).getTime();
  // An unparseable timestamp is treated as "never reminded". The alternative is a
  // NaN comparison, which is always false — silently muting the prompt forever.
  if (Number.isNaN(remindedAt)) return true;

  return now - remindedAt >= RENEWAL_REMINDER_INTERVAL_MS;
}

export interface BanUserRequest {
  reason: string;
  /** Omitted means indefinite. */
  days?: number;
}

export interface ActiveBan {
  reason: string | null;
  bannedUntil: string | null;
}

/** The 403 body a banned caller gets from every authenticated endpoint. */
export const ACCOUNT_BANNED_ERROR = 'ACCOUNT_BANNED';

export interface AdminStats {
  totalUsers: number;
  newUsers: number;
  activeSubscriptions: number;
  totalGenerations: number;
  generationsInPeriod: number;
  revenueInPeriod: number;
  planDistribution: Partial<Record<PlanType, number>>;
  activityByType: Partial<Record<ActivityType, number>>;
  generationsPerDay: { date: string; count: number }[];
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}
